/* eslint-disable max-lines-per-function */
/* eslint-disable max-lines */
import React, {useEffect, useRef, useState} from 'react';
import {Link, useHistory, useParams} from 'react-router-dom';
import _ from 'lodash';
import {
  CreateUserMessageType,
  CreateUserStatus,
  LoginType,
  // NOTE: This is temporary as change to new permissions structure will eventually remove the old usage of user role assignments
  // i.e. RoleDto
  RoleDto,
  FacilityRoleDto,
  UserInvitationDto,
  UserRoleAssignmentDto,
  OidcProviderDto, OidcProviderType
} from "server-openapi";
import styled from 'styled-components';
import {NavigationBar} from '../../../components/NavigationBar/NavigationBar';
import {apis} from '../../../core/mrs/apis';
import {Button, Variation} from '../../../kit/Button';
import {StyledTextInput, TextInput} from '../../../kit/Forms/TextInput';
import {Grid} from '../../../kit/Grid';
import {useAsync} from '../../../kit/hooks/UseAsync';
import {IconBack} from '../../../kit/Icons/Back';
import {useForm, useFormContext} from 'react-hook-form';
import {Form} from '../../../kit/Forms/Form';
import {toasts} from '../../../kit/Toasts/Toaster';
import {format} from 'date-fns';
import {Layout} from '../../../kit/Layout';
import {RolesDetailsSection} from './components/RolesDetailsSection';
import {Intent} from '../../../kit/Theme/Theme';
import {DateUtils} from '../../../core/utils/dateUtils';
import {RequirePermission} from '../../../components/RequirePermission/RequirePermission';
import {ValidationMessages} from '../../../core/utils/validationMessages';
import {Select, SelectOption} from "../../../kit/Forms/Select";
import {useGroupPermissions} from "../../../core/authz/PermissionsProvider";
import {useSyncCenter} from '../../../syncstream/SyncCenterProvider';
import {useStore} from '../../../core/storage/hooks/UseStore';
import {Tooltip} from "react-tooltip";
import {IoInformationCircle} from "react-icons/io5";
import { MsFacilityRoleAssignmentDto } from "server-openapi/dist/generated-sdk/api";
import { FacilityRolesDetailsSection } from "./components/FacilityRolesDetailsSection";

interface IParams {
  facilityGroupId: string;
  userId: string;
}

// NOTE: This is temporary as change to new permissions structure will eventually remove the old usage of user role assignments
// i.e. RoleDto
export interface RoleAccessDto {
  identifier: number | undefined;
  role: RoleDto;
  startDate: Date;
  expiryDate: Date | undefined;
  softDelete: boolean;
  isNew: boolean;
}

export interface FacilityRoleAccessDto {
    identifier: number | undefined;
    facilityRole: FacilityRoleDto;
    startDate: Date;
    expiryDate: Date | undefined;
    active: boolean;
    isNew: boolean;
}

export type UserDetailsFormValues = {
  firstName: string;
  familyName: string;
  email: string;
  loginType: LoginType;
  designation: string;
  oidcProviderId: number;
  roleAccess: RoleAccessDto[];
  facilityRoleAccess: FacilityRoleAccessDto[];
  // The Subject ID input is read only and is never sent to the API, but it is still a
  // react-hook-form controlled input so it needs to be part of the form values in order for
  // form.setValue() to be able to update what it displays.
  'subject-id': string;
};

export function UserDetailsPage() {
  const groupPermissions = useGroupPermissions();
  return (
    <Grid cols={1} gap={1}>
      <RequirePermission hasPermission={groupPermissions.canViewFacilityGroupConfigurationIncludingUserManagement}>
        <UserDetailContent/>
      </RequirePermission>
    </Grid>
  );
}

function UserDetailContent() {
  const facilityGroupId = parseInt(useParams<IParams>().facilityGroupId);
  const userId = parseInt(useParams<IParams>().userId);
  const isNewUser = useParams<IParams>().userId === 'new';
  const history = useHistory();
  const previousPagePath = `/admin/facility-group/${facilityGroupId}/settings/users`;
  const [user, setUser] = useState<UserInvitationDto>();

  // NOTE: This is temporary as change to new permissions structure will eventually remove the old usage of user role assignments
  const [useUserPermissionsFromHs, setUseUserPermissionsFromHs] = useState<boolean>();
  const [roles, setRoles] = useState<RoleDto[]>([]);
  const [facilityRoles, setFacilityRoles] = useState<FacilityRoleDto[]>([]);
  const [oidcProviders, setOidcProviders] = useState<OidcProviderDto[]>([]);

  useAsync(async () => {
    const usePermsFromHs =
      (await apis.GlobalSettingsApi.globalSettingsUseUserPermissionsFromHs(facilityGroupId)).data;

    setUseUserPermissionsFromHs(usePermsFromHs);

    if (usePermsFromHs) {
      setFacilityRoles((await apis.facilityRoles.facilityRoleGetFacilityRolesForUser(facilityGroupId)).data);
    } else {
      setRoles((await apis.roles.roleGetAll(facilityGroupId)).data);
    }

    const oidcProviderResponse = isNewUser
      ? await apis.facilityAllowedIdp.facilityAllowedIdpGetOidcProvidersForFacilityGroup(facilityGroupId)
      : await apis.facilityAllowedIdp.facilityAllowedIdpGetOidcProvidersForFacilityGroupAndUser(facilityGroupId, userId);
    setOidcProviders(oidcProviderResponse.data ?? []);

  }, [facilityGroupId, userId]);

  useAsync(async () => {
    if (!isNewUser) {
      const response = (await apis.users.userGetUserInvitationById(userId)).data;
      setUser(response);
    }
  });

  function goToPreviousPage() {
    history.push(previousPagePath);
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity
  async function onSubmit(data: UserDetailsFormValues) {
    // NOTE: This is temporary as change to new permissions structure will eventually remove the old filter by role assignments
    if (useUserPermissionsFromHs) {
      data.facilityRoleAccess.forEach((role) => {
        // Refer to line 92 in UserRolesDetails.tsx
        if (role.expiryDate?.toString() === 'Invalid Date') {
          role.expiryDate = undefined;
        }
      });
    }
    else
    {
      data.roleAccess.forEach((role) => {
        // Refer to line 92 in UserRolesDetails.tsx
        if (role.expiryDate?.toString() === 'Invalid Date') {
          role.expiryDate = undefined;
        }
      });
    }

    const defaultOidcProviderId = oidcProviders.find((provider) => provider.type ===  OidcProviderType.AzureAdb2C)?.id;
    const {firstName, familyName, email, designation, roleAccess, facilityRoleAccess, loginType} = data;
    const oidcProviderId = data.oidcProviderId ?? defaultOidcProviderId;
    const selectedOidcProvider = oidcProviders.find((provider) => provider.id?.toString() === oidcProviderId?.toString());
    const hasInvalidLoginType = !loginType || loginType.toString() === 'NotUsed';

    if (selectedOidcProvider?.type === OidcProviderType.AzureAdb2C && hasInvalidLoginType) {
      form.setError('loginType', {
        type: 'required',
        message: 'Login Type is required'
      });
      return;
    }

    console.log({
      dataOidcProviderId: data.oidcProviderId,
      userOidcProviderId: user?.oidcProviderId,
      oidcProviderId,
      loginType
    });

    // NOTE: This is temporary as change to new permissions structure will eventually remove the old filter by role assignments
    const userRoleAssignments =
        roleAccess?.filter(r => r.role?.urn && (!r.softDelete || !r.isNew)).map((r): UserRoleAssignmentDto => {
        return {
          id: r.identifier,
          roleUrn: r.role.urn!,
          startAt: DateUtils.fromDate(r.startDate),
          endAt: r.expiryDate ? DateUtils.fromDate(r.expiryDate) : undefined,
          softDelete: r.softDelete
        };
      }) ?? [];

    const facilityRoleAssignments =
        facilityRoleAccess?.filter(fr => fr.facilityRole?.hsId && !fr.isNew).map((fr): MsFacilityRoleAssignmentDto => {
        return {
          id: fr.identifier,
          facilityRoleId: fr.facilityRole.hsId!,
          facilityRoleName: fr.facilityRole.name!,
          facilityId: facilityGroupId, // This is because a HS facility is a facility group in MS  
          startAtInclusive: DateUtils.fromDate(fr.startDate),
          endAtExclusive: fr.expiryDate ? DateUtils.fromDate(fr.expiryDate) : undefined,
          active: fr.active
        };
      }) ?? [];

      if (!isNewUser) {
      await apis.users.userUpdateUserInvitation({
        facilityGroupId: facilityGroupId,
        id: userId,
        loginType: loginType,
        oidcProviderId: oidcProviderId,
        designation,
        userRoleAssignments,
        facilityRoleAssignments
      }).then(() => {
        toasts.success(`${user?.firstName} ${user?.familyName} has been ${isNewUser ? 'added' : 'updated'} successfully`);
        goToPreviousPage();
      }).catch(err => {
        const responseStatus = err.response?.status;

        // Hack so that handle when get BadHttpRequestException (400) we can show a specific targeted message.
        let errorMessage = responseStatus === 400 ? 'External Azure AD B2C error while processing user' : 'Unknown errors';

        toasts.error(`Error updating user: ${errorMessage}`);
      });
    }
    else {
      const userToInvite = {
        email,
        firstName,
        familyName,
        loginType,
        designation,
        oidcProviderId,

        // NOTE: This is temporary as change to new permissions structure will eventually remove the old usage of user role assignments
        userRoleAssignments,
        facilityRoleAssignments
      };
      await apis.users.userInviteUser({
        facilityGroupId: facilityGroupId,
        userDetails: userToInvite
      }).then((resp) => {
        if (resp.data.createUserStatus === CreateUserStatus.Created) {
          goToPreviousPage();
        }
        for (const msg of (resp.data.messages ?? [])) {
          if (msg.message) {
            let intent: Intent = Intent.Success;
            let timeout = 5;
            if (msg.messageType === CreateUserMessageType.Warning) {
              intent = Intent.Warning;
              timeout = 10;
            } else if (msg.messageType === CreateUserMessageType.Error) {
              intent = Intent.Danger;
              timeout = 10;
            }
            toasts.toast(msg.message, {
              intent: intent,
              timeoutSeconds: timeout
            });
          }
        }
      }).catch(err => {
        toasts.error(`Error updating user: ${err}`);
      });
    }

  }

  // NOTE: This is temporary as change to new permissions structure will eventually remove the old usage of user role assignments
  // All FacilityRoleAssignments are considered active here, in the sense that they are not soft deleted. They get actually deleted.
  // So even though they have an active flag on the records ,it doesnt relate to how the soft delete used here for the older user role assignments.
  // We still want to be able to show them here when their active flag is false.
  const activeUserRoleAssignments = user?.userRoleAssignments?.filter((ura) => !ura.softDelete);
  const activeFacilityRoleAssignments = user?.facilityRoleAssignments;

  const form = useForm<UserDetailsFormValues>({
    mode: 'all',
    reValidateMode: 'onSubmit',
    shouldUnregister: false, // entering value into Designation field removes newly added role field (created by clicking on +Add Additional Role button). This prevents the issue.
    defaultValues: {
      oidcProviderId: user?.oidcProviderId ?? undefined
    }
  });

  useEffect(() => {
    form.setValue('firstName', user?.firstName ?? '');
    form.setValue('familyName', user?.familyName ?? '');
    form.setValue('email', user?.email ?? '');
    form.setValue('designation', user?.designation ?? '');

      // NOTE: This is temporary as change to new permissions structure will eventually remove the old usage of user role assignments
    form.setValue(
      'roleAccess',
      activeUserRoleAssignments?.map((roleAssignment) => {
        const role = roles.find((r) => r.urn === roleAssignment.roleUrn);

        const startAt = roleAssignment?.startAt;
        const endAt = roleAssignment?.endAt;

        return {
          identifier: roleAssignment.id,
          role: role,
          startDate: startAt ? DateUtils.toDate(startAt) : undefined,
          expiryDate: endAt ? DateUtils.toDate(endAt) : undefined,
          softDelete: roleAssignment?.softDelete ?? false,
          isNew: false
        };
      })
    );
    form.setValue(
      'facilityRoleAccess',
      activeFacilityRoleAssignments?.map((facilityRoleAssignment) => {
        const facilityRole = facilityRoles.find((fr) => fr.hsId === facilityRoleAssignment.facilityRoleId);

        const startAt = facilityRoleAssignment?.startAtInclusive;
        const endAt = facilityRoleAssignment?.endAtExclusive;

        return {
          identifier: facilityRoleAssignment.id,
          facilityRole: facilityRole,
          startDate: startAt ? DateUtils.toDate(startAt) : undefined,
          expiryDate: endAt ? DateUtils.toDate(endAt) : undefined,
          active: facilityRoleAssignment?.active ?? true,
          isNew: false
        };
      })
    );
  }, [user, roles, facilityRoles]);


  return (
    <>
      <NavigationSection user={user} isNewUser={isNewUser} previousPagePath={previousPagePath}/>
      <Layout gap={1} padding="20px 50px">
        <Form form={form} onSubmit={onSubmit}>
          <UserDetailsSection
              user={user}
              isNewUser={isNewUser}
              setUser={setUser}
              facilityGroupId={facilityGroupId}
              oidcProviders={oidcProviders}
              setOidcProviderId={(oidcProviderId) => form.setValue('oidcProviderId', oidcProviderId)} // This form.setValue is because it loads the options async
          />
          {/* NOTE: This is temporary as change to new permissions structure will eventually remove the old filter by role assignments*/}
          {!useUserPermissionsFromHs && user && roles && (
            <RolesDetailsSection user={user} roles={roles} form={form} facilityGroupId={facilityGroupId}/>
          )}
          {useUserPermissionsFromHs && user && facilityRoles && (
            <FacilityRolesDetailsSection user={user} facilityRoles={facilityRoles} form={form} facilityGroupId={facilityGroupId}/>
          )}
          <PageNavigationButtonContainer cols={2} gap={1}>
            <Button style={{width: '250px'}} variation={Variation.outline} intent={Intent.Secondary}
                    onClick={goToPreviousPage} fullWidth>
              CANCEL
            </Button>
            <Button style={{width: '250px'}} type="submit" fullWidth>
              SAVE
            </Button>
          </PageNavigationButtonContainer>
        </Form>
      </Layout>
    </>
  );
}

function NavigationSection(props: { user?: UserInvitationDto; isNewUser: boolean; previousPagePath: string }) {
  const fullName = (user?: UserInvitationDto) => {
    if (!!user)
      return user.firstName + ' ' + user.familyName;
    else {
      return "";
    }
  };


  return (
    <NavigationBar
      nodes={() => ([
        <Grid key="user-details-navigation-title" colsTemplate="35px 1fr">
          <Link to={props.previousPagePath}>
            <IconBack/>
          </Link>
          <h1>{props.isNewUser ? 'New User' : fullName(props.user)}</h1>
        </Grid>
      ])}
    />
  );
}

// eslint-disable-next-line sonarjs/cognitive-complexity
function UserDetailsSection(props: {
  user?: UserInvitationDto;
  isNewUser: boolean;
  setUser: (user: UserInvitationDto) => void;
  facilityGroupId: number;
  oidcProviders?: OidcProviderDto[];
  setOidcProviderId: (oidcProviderId: number | undefined) => void;
}) {
  const {user, isNewUser, setUser, oidcProviders = [], setOidcProviderId} = props;
  const [selectedOidcProviderId, setSelectedOidcProviderId] = useState<number | undefined>(user?.oidcProviderId ?? undefined);
  const [hasAppliedDefaultOidcProvider, setHasAppliedDefaultOidcProvider] = useState(false);
  const form = useFormContext<UserDetailsFormValues>();

  // Temp storage for the Subject ID and Login Type that were on display just before an Azure AD B2C
  // provider was selected. They are held here (rather than in state) so that they survive re-renders
  // without causing any, and so we can put the original values back if the user then picks a provider
  // that is not Azure AD B2C.
  // hasStashedSubjectIdAndLoginType is tracked separately because the stashed values themselves can
  // legitimately be undefined, so their value alone cannot tell us whether a swap has happened.
  const stashedSubjectId = useRef<string | null | undefined>(undefined);
  const stashedLoginType = useRef<LoginType | undefined>(undefined);
  const hasStashedSubjectIdAndLoginType = useRef(false);
  const services = useSyncCenter();
  const facilityGroupConfigurationStore = useStore(services.facilityGroupConfigurations.store).store;
  const loginTypes = facilityGroupConfigurationStore.get(props.facilityGroupId.toString())?.loginTypes;
  const loginTypeOptions = loginTypes?.map((t) => ({
    label: _.startCase(_.toLower(t)),
    value: LoginType[_.startCase(_.toLower(t)) as keyof typeof LoginType]
  } as SelectOption<LoginType>));

  const oidcProviderOptions = oidcProviders
      .filter((provider) => provider.id !== undefined)
      .map((provider) => ({
        label: provider.description ?? '',
        value: provider.id!
      } as SelectOption<number>));

  const defaultOidcProviderId = oidcProviders.find((provider) => provider.type === OidcProviderType.AzureAdb2C)?.id;
  const selectedOidcProvider = oidcProviders.find((provider) => provider.id === selectedOidcProviderId);
  const isAzureAdb2CProviderSelected = selectedOidcProvider?.type === OidcProviderType.AzureAdb2C;

  /**
   * Pushes the given Subject ID / Login Type into the form state and returns the equivalent user
   * fields so the caller can merge them into its setUser() call.
   *
   * Both inputs are react-hook-form controlled fields, which means what they render comes from the
   * form state and not from the user object, so updating the user alone would leave the inputs
   * showing the old values - hence the form.setValue() calls here.
   */
  function applySubjectIdAndLoginType(
      subjectId: string | null | undefined,
      loginType: LoginType | undefined
  ): Partial<UserInvitationDto> {
    // 'NotUsed' is the API's "no login type recorded" marker, so show it as nothing selected
    // (same handling as the Login Type select's own value below).
    const loginTypeToApply = !loginType || loginType.toString() === 'NotUsed' ? undefined : loginType;

    form.setValue('subject-id', subjectId ?? '');
    // The Login Type select is only mounted while an Azure AD B2C provider is selected. Setting the
    // value while it is unmounted is still safe because the form is created with
    // shouldUnregister: false, so the value is retained and picked up when the select mounts again.
    form.setValue('loginType', loginTypeToApply as LoginType);

    return {subjectId: subjectId ?? undefined, loginType: loginTypeToApply};
  }

  /**
   * Keeps the Subject ID and Login Type in step with the kind of OIDC provider being selected.
   *
   * Switching *to* an Azure AD B2C provider: the values currently on display are stashed away and
   * replaced with the user's azureAdB2cSubjectId / lastValidLoginType from the DTO, which are the
   * details that actually apply once the user is moved onto Azure AD B2C.
   * Switching *away* from Azure AD B2C: the stashed values are restored.
   *
   * Returns the user fields that changed (empty when no swap is needed) so the caller can apply them
   * in the one setUser() call it already makes for the provider change - calling setUser() twice from
   * the same handler would just overwrite the first update with stale state.
   */
  function swapSubjectIdAndLoginTypeForProvider(newOidcProviderId: number | undefined): Partial<UserInvitationDto> {
    // Only applies when editing an existing user. A new user has no persisted Azure AD B2C details to
    // swap in, and we must not wipe the Login Type they have just chosen.
    if (isNewUser) {
      return {};
    }

    const isAzureAdb2CNowSelected =
        oidcProviders.find((provider) => provider.id === newOidcProviderId)?.type === OidcProviderType.AzureAdb2C;

    if (isAzureAdb2CNowSelected) {
      // Already showing the Azure AD B2C values - selecting another Azure AD B2C provider must not
      // stash them over the top of the originals.
      if (hasStashedSubjectIdAndLoginType.current) {
        return {};
      }

      stashedSubjectId.current = user?.subjectId;
      stashedLoginType.current = user?.loginType;
      hasStashedSubjectIdAndLoginType.current = true;

      return applySubjectIdAndLoginType(user?.azureAdB2cSubjectId, user?.lastValidLoginType);
    }

    // Moving to a non Azure AD B2C provider. There is only something to restore if we previously
    // swapped the Azure AD B2C values in - if the user was already on Azure AD B2C when the page
    // loaded then what is on display is their persisted detail and should be left alone.
    if (!hasStashedSubjectIdAndLoginType.current) {
      return {};
    }

    hasStashedSubjectIdAndLoginType.current = false;

    return applySubjectIdAndLoginType(stashedSubjectId.current, stashedLoginType.current);
  }

  useEffect(() => {
    const userOidcProviderId = user?.oidcProviderId ?? undefined;

    if (userOidcProviderId !== undefined && userOidcProviderId !== selectedOidcProviderId) {
      setHasAppliedDefaultOidcProvider(true);
      setSelectedOidcProviderId(userOidcProviderId);
      setOidcProviderId(userOidcProviderId);
    }
  }, [selectedOidcProviderId, setOidcProviderId, user?.oidcProviderId]);

  useEffect(() => {
    if (
        isNewUser &&
        !hasAppliedDefaultOidcProvider &&
        selectedOidcProviderId === undefined &&
        defaultOidcProviderId !== undefined
    ) {
      setHasAppliedDefaultOidcProvider(true);
      setSelectedOidcProviderId(defaultOidcProviderId);
      setOidcProviderId(defaultOidcProviderId);
      setUser({...user, oidcProviderId: defaultOidcProviderId});
    }
  }, [
    defaultOidcProviderId,
    hasAppliedDefaultOidcProvider,
    isNewUser,
    selectedOidcProviderId,
    setOidcProviderId,
    setUser,
    user
  ]);

  if (!loginTypes) throw new Error('No Login Types found in Facility Configurations for this facilityGroup. Looks like the Facility Configurations was not synced.');

  if (!user && !isNewUser) {
    return null;
  }

  return (
    <>
      <HeaderText>User Details</HeaderText>
      <SectionInputsContainer>
        <Grid colsTemplate="1fr 1fr">
          {isNewUser && (
            <>
              <Layout gap={0.5}>
                <div>First Name <span style={{color: 'red'}}>*</span></div>
                <TextInput
                  name="firstName"
                  value={user?.firstName ?? ''}
                  onChange={(_, value) => setUser({...user, firstName: value})}
                  autoComplete="off"
                  fullWidth
                  required
                />
              </Layout>
              <Layout gap={0.5}>
                <div>Family Name <span style={{color: 'red'}}>*</span></div>
                <TextInput
                  name="familyName"
                  value={user?.familyName ?? ''}
                  onChange={(_, value) => setUser({...user, familyName: value})}
                  fullWidth
                  required
                  autoComplete="off"
                />
              </Layout>
              <Layout gap={0.5}>
                <div>Email <span style={{color: 'red'}}>*</span></div>
                <TextInput
                  name="email"
                  value={user?.email ?? ''}
                  onChange={(_, value) => setUser({...user, email: value})}
                  fullWidth
                  required
                  autoComplete="off"
                  rules={{
                    validate: (email: string) =>
                      ValidationMessages.email.regex!.test(email) || ValidationMessages.email.message,
                  }}
                />
              </Layout>
            </>
          )}
          {!isNewUser && (
            <>
              <Layout gap={0.5}>
                <div>Name</div>
                <StyledTextInput value={`${user?.firstName} ${user?.familyName}`} fullWidth disabled/>
              </Layout>
              <Layout gap={0.5}>
                <div>Email</div>
                <StyledTextInput value={user?.email ?? ''} fullWidth disabled/>
              </Layout>
              <Layout gap={0.5}>
                <div>Date Added</div>
                <StyledTextInput
                  value={user?.createdAt ? format(DateUtils.toDate(user.createdAt), 'd MMMM yyyy') : ''}
                  fullWidth
                  disabled
                />
              </Layout>
            </>
          )}
          <Layout gap={0.5}>
            <div>OIDC Provider <span style={{color: 'red'}}>*</span></div>
            <Select
                name="oidcProviderId"
                options={oidcProviderOptions}
                value={selectedOidcProviderId}
                onChange={(_, value) => {
                  // Swap the Subject ID / Login Type over first - this needs to run while
                  // selectedOidcProviderId still holds the previously selected provider so it can
                  // tell which way the selection is moving.
                  const swappedUserDetails = swapSubjectIdAndLoginTypeForProvider(value);

                  setHasAppliedDefaultOidcProvider(true);
                  setSelectedOidcProviderId(value);
                  setOidcProviderId(value);
                  setUser({...user, oidcProviderId: value, ...swappedUserDetails});
                }}
                fullWidth
                required
                disabled={oidcProviderOptions.length === 0}
            />
          </Layout>
          {isAzureAdb2CProviderSelected && (
            <Layout gap={0.5}>
              <Tooltip id="login-type-tooltip"/>
              <div style={{display: 'flex'}}>
                Login Type
                <IoInformationCircle data-tooltip-id="login-type-tooltip"
                  data-tooltip-html="<div>Local: Uses a personally managed email account.</div><div>Sso (Single Sign On): Uses a corporate managed email account.</div>"
                  size='20px' style={{marginLeft: '5px'}}/>
              </div>
              <Select
                  name="loginType"
                  options={loginTypeOptions!}
                  value={user?.loginType?.toString() === 'NotUsed' ? undefined : user?.loginType ?? undefined}
                  onChange={(_, value) => {
                    setUser({...user, loginType: value});
                  }}
                  fullWidth
                  required
              />
            </Layout>
          )}
          <Layout gap={0.5}>
            <div>Designation <span style={{color: 'red'}}>*</span></div>
            <TextInput
              name="designation"
              autoComplete="off"
              value={user?.designation ?? ''}
              onChange={(_, value) => setUser({...user, designation: value})}
              fullWidth
              required
            />
          </Layout>
          {!isNewUser && (
            <Layout gap={0.5}>
              <div>Subject ID</div>
              <TextInput
                name="subject-id"
                value={user?.subjectId ?? ''}
                fullWidth
                disabled
                autoComplete="off"
              />
            </Layout>
          )}
        </Grid>
      </SectionInputsContainer>
    </>
  );
}

const HeaderText = styled.h2`
    margin-left: 20px;
`;

const SectionInputsContainer = styled(Layout)`
    background: ${(p) => p.theme.backgrounds.lighter.bg};
    padding: 20px;
    margin-bottom: 2em
`;

const PageNavigationButtonContainer = styled(Grid)`
    background-color: #FFFFFF;
    width: 100%;
    position: fixed;
    display: flex;
    justify-content: flex-end;
    bottom: 0;
    right: 0;
    padding: 25px 50px;
`;
